import { faEdit, faEye, faListUl, faSquarePlus } from "@fortawesome/pro-solid-svg-icons";
import { batch } from "@preact/signals-react";
import { clone, cloneArray } from "@progress/kendo-react-common";
import { Window } from "@progress/kendo-react-dialogs";
import { ListView } from "@progress/kendo-react-listview";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  galleryConstructorGridRows,
  galleryConstructorVisibility,
} from "../Common/GalleryConstructor/GalleryConstructorSignals";
import {
  allTemplates,
  getEntriesByTemplateId,
  splitDeleteAndCreateEnabledDataset,
  splitEnabledDataset,
  userData,
  userEmail,
  userTemplates,
} from "../Common/Store/CommonSignals";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { datasetData } from "../Datasets/NewDatasetSignals";
import {
  disabledSections,
  entryId,
  entryValues,
  formErrors,
  galleryValues,
  initialCommentsLengths,
  isEntryPreview,
  readOnlyEntry,
  selectedStep,
  selectedTab,
  steps,
  template,
  templateId,
  templateStageSteps,
} from "../Entry/Manage/EditEntrySignals";
import "../Pages/Homepage.css";
import { templateData } from "../Template/NewTemplateSignals";
import {
  getConnectionData,
  getEntryValues,
  setInitialGalleryValues,
  validateStageStep,
} from "../Utilities/EntryUtils";

const ValueItemRender = ({ dataItem }) => <div className="list-">{dataItem}</div>;

const RenderFields = ({ fields }) => {
  if (!fields) {
    return null;
  }

  return (
    <>
      {fields.map((field) => (
        <div
          key={uuidv4()}
          className="list-"
        >
          {field.fieldName} ({field.fieldType})
        </div>
      ))}
    </>
  );
};

const SectionItemRender = ({ dataItem }) => (
  <div>
    <div>
      <strong>Name:</strong> {dataItem.sectionName}
    </div>
    <RenderFields fields={dataItem.fields} />
  </div>
);

const TemplateRenderer = ({ template }) => {
  if (!template.sections) {
    return <div>No data available.</div>;
  }

  return (
    <ListView
      data={template.sections}
      item={SectionItemRender}
    />
  );
};

export const EditDatasetButton = (props) => {
  const navigate = useNavigate();
  return (
    <ColorChangingIcon
      icon={faEdit}
      initialPrimaryColor="#0078D4"
      padding="0 2.5% 0 2.5%"
      hidden={props.dataItem.permission === "View Only"}
      onClick={() => {
        batch(() => {
          const datasetValues = props.dataItem?.values.map((item) => {
            return {
              id: uuidv4(),
              ...item,
            };
          });
          const currDataset = {
            id: props.dataItem.id,
            datasetName: props.dataItem.datasetName,
            datasetDescription: props.dataItem.datasetDescription,
            datasetType: props.dataItem.datasetType,
            values: datasetValues,
            columns: [],
            childTable: null,
            parentDataset: props.dataItem.parentDataset,
            owner: props.dataItem.owner,
            sharedPermission: props.dataItem.sharedPermission,
            coOwners: props.dataItem.coOwners,
            tags: props.dataItem.tags || [],
          };
          datasetData.value = currDataset;
        });
        navigate(`/datasets/edit?datasetId=${props.dataItem.id}`);
      }}
    />
  );
};

export const EditTemplateButton = (props) => {
  const navigate = useNavigate();
  return (
    <ColorChangingIcon
      icon={faEdit}
      initialPrimaryColor="#0078D4"
      padding="0 2.5% 0 2.5%"
      hidden={
        props.dataItem.permission === "View Only" ||
        props.dataItem.permission === "Make Entry" ||
        props.dataItem.permission === "Make Entry (Modify All)" ||
        props.dataItem.permission === "ViewAuthorOnly"
      }
      onClick={() => {
        batch(() => {
          galleryConstructorVisibility.value = false;
          galleryConstructorGridRows.value = [];

          //Update the columns of the template data connections
          const dataConnections = cloneArray(props.dataItem.properties.DataConnections ?? []);
          const updatedTemplate = {
            ...props.dataItem,
            properties: {
              ...props.dataItem.properties,
              DataConnections: dataConnections?.map((dataConnection) => {
                console.log(dataConnection);
                let columns = {};
                if (dataConnection.type === "Template") {
                  const selectedTemplate = userTemplates.value?.find((template) => {
                    return template.id === dataConnection.id;
                  });
                  columns["id"] = { selected: true };
                  columns["createdDate"] = { selected: true };
                  columns["_ts"] = { selected: true };
                  columns["entryAuthor"] = { selected: true };
                  selectedTemplate?.sections?.forEach((section) => {
                    section.fields?.forEach((field) => {
                      columns[field.fieldName] = { selected: true };
                    });
                  });

                  if (dataConnection.columns) {
                    Object.entries(dataConnection.columns).forEach(([key, value]) => {
                      if (Object.hasOwn(columns, key)) {
                        columns[key] = { ...columns[key], ...value };
                      }
                    });
                  }
                } else {
                  columns = dataConnection.columns || {};
                }

                return {
                  ...dataConnection,
                  columns: clone(columns),
                };
              }),
            },
          };

          templateData.value = updatedTemplate;
        });
        navigate(`/templates/edit?templateId=${props.dataItem.id}`);
      }}
    />
  );
};

const getRevisionCount = (obj, fieldName) => {
  for (const section of obj.sections) {
    for (const field of section.fields) {
      if (field.fieldName === fieldName) {
        return field.revisionCount ?? 0;
      }
    }
  }
  return "Rev # Not Found";
};

export const CreateEntryTemplateButton = (props) => {
  const navigate = useNavigate();

  const shouldBeHidden = (id) => {
    const splitTemplatePermission =
      splitEnabledDataset.value?.values?.some((item) => item.value === id) &&
      !splitDeleteAndCreateEnabledDataset.value?.values?.some(
        (item) => String(item.value).toLowerCase() === userEmail.value.toLowerCase()
      );
    const viewOnlyPermission = props.dataItem.permission === "View Only";
    return splitTemplatePermission || viewOnlyPermission;
  };

  return (
    <ColorChangingIcon
      icon={faSquarePlus}
      initialPrimaryColor="#0078D4"
      padding="0 2.5% 0 0"
      hidden={shouldBeHidden(props.dataItem.id)}
      onClick={() => {
        batch(() => {
          galleryValues.value = [];
          templateId.value = props.dataItem.id;
          template.value = allTemplates.value.find((x) => x.id === props.dataItem.id);
          const updatedSections = template.value.sections.map((section, sIndex) => ({
            ...section,
            properties: section.properties || {},
            fields: section.fields.map((field, fIndex) => {
              const fieldType = field.fieldType;
              let fieldValue;
              if (fieldType === "BusinessUnit") {
                fieldValue = userData.value?.officeLocation;
              } else if (fieldType === "Checkbox") {
                fieldValue = "false";
              } else if (
                typeof field.properties?.DefaultValue === "boolean" &&
                fieldType.includes("Date")
              ) {
                const newDate = new Date();
                if (field.properties?.HoursOnly) {
                  newDate.setMinutes(0);
                  newDate.setSeconds(0);
                  newDate.setMilliseconds(0);
                }
                const timezoneOffset = new Date().getTimezoneOffset() * 60000;
                const convertedDate = new Date(newDate.getTime() - timezoneOffset)
                  .toISOString()
                  .replace("Z", "");
                fieldValue = field.properties?.DefaultValue ? convertedDate : "";
              } else if (fieldType === "Time") {
                const newDate = new Date();
                const timezoneOffset = new Date().getTimezoneOffset() * 60000;
                const convertedDate = new Date(newDate.getTime() - timezoneOffset)
                  .toISOString()
                  .replace("Z", "");
                fieldValue = field.properties?.DefaultValue ? convertedDate : "";
              } else {
                fieldValue = field.properties?.DefaultValue ?? "";
              }
              if (fieldType === "Gallery") {
                const initialGalleryValues = setInitialGalleryValues({
                  galleryName: field.fieldName,
                  values: getConnectionData({
                    field,
                    Data: field.properties?.Data,
                  }),
                  fields: field.properties?.Fields,
                  field: field,
                });

                entryValues.value = {
                  ...entryValues.value,
                  [field.fieldName]: initialGalleryValues,
                };

                const newTemplateView = JSON.tryParse(JSON.stringify(template.value));
                let newField = {
                  ...newTemplateView.sections[sIndex].fields[fIndex],
                  Values: initialGalleryValues,
                };
                newTemplateView.sections[sIndex].fields[fIndex] = newField;

                template.value = newTemplateView;

                return {
                  ...field,
                  value: "",
                  properties: {
                    ...field.properties,
                    Values: initialGalleryValues,
                  },
                };
              }
              if (field.properties?.["RevisionCount"]) {
                return {
                  ...field,
                  value: fieldValue,
                  revisionCount: getRevisionCount({ ...template.value }, field.fieldName),
                };
              }
              return {
                ...field,
                value: fieldValue,
              };
            }),
          }));
          template.value = { ...template.value, sections: updatedSections };
          entryValues.value = getEntryValues(template.value);
          entryId.value = null;
          formErrors.value = {};
          disabledSections.value = {};
          selectedTab.value = 0;
          initialCommentsLengths.value = {};
          isEntryPreview.value = false;
          selectedStep.value = 0;
          steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
          readOnlyEntry.value = false;
        });
        validateStageStep();
        navigate(`/new-entry?templateId=${templateId.value}`);
      }}
    />
  );
};

export const ViewEntryTemplateButton = (props) => {
  const navigate = useNavigate();
  return (
    <ColorChangingIcon
      label="View Entries"
      icon={faListUl}
      initialPrimaryColor="#0078D4"
      padding="0 2.5% 0 0"
      onClick={() => {
        templateId.value = props.dataItem.id;
        getEntriesByTemplateId(props.dataItem.id);
        if (props.dataItem?.properties?.ChildTemplate?.id) {
          getEntriesByTemplateId(props.dataItem?.properties?.ChildTemplate?.id, "initial");
          getEntriesByTemplateId(props.dataItem?.properties?.ChildTemplate?.id, "all");
        }
        navigate(`/view-entry?templateId=${props.dataItem.id}`);
      }}
    />
  );
};

export const ViewEntryLink = (props) => {
  const navigate = useNavigate();
  return (
    <td>
      <a
        href="/"
        onClick={(event) => {
          templateId.value = props.dataItem.id;
          getEntriesByTemplateId(props.dataItem.id);
          event.preventDefault(); // Prevent default navigation
          navigate(`/view-entry?templateId=${props.dataItem.id}`);
        }}
      >
        {props.dataItem.templateName}
      </a>
    </td>
  );
};

export const EditEntryButton = (props) => {
  const navigate = useNavigate();
  return (
    <ColorChangingIcon
      icon={faEdit}
      initialPrimaryColor="#0078D4"
      padding="0 2.5% 0 2.5%"
      hidden={props.dataItem.permission === "View Only"}
      onClick={() =>
        navigate(`/edit-entry?entryId=${props.dataItem.id}&templateId=${props.dataItem.templateId}`)
      }
    />
  );
};

export const ViewDatasetButton = (props) => {
  const [visible, setVisible] = useState(false);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  return (
    <>
      <ColorChangingIcon
        icon={faEye}
        initialPrimaryColor="#0078D4"
        onClick={toggleDialog}
      />

      {visible && (
        <Window
          title={props.dataItem.datasetName}
          onClose={toggleDialog}
          initialHeight={500}
          initialWidth={500}
        >
          <div className="rowView">
            <div>
              <strong>Description:</strong> {props.dataItem.datasetDescription}{" "}
            </div>
            <div>
              <strong>Type:</strong> {props.dataItem.datasetType}{" "}
            </div>
            <div>
              <strong>Owner:</strong> {props.dataItem.owner}{" "}
            </div>
            <div>
              {props.dataItem.coOwners && (
                <div>
                  <strong>Co-Owners:</strong>{" "}
                  {props.dataItem.coOwners.map((coOwner) => (
                    <div key={coOwner.user}>{coOwner.user}</div>
                  ))}
                </div>
              )}
            </div>
            <div>
              {props.dataItem.sharedPermission && (
                <div>
                  <strong>View Permissions: </strong>
                  {props.dataItem.sharedPermission.filter((sp) => sp.permission === "View").length >
                  0 ? (
                    props.dataItem.sharedPermission
                      .filter((sp) => sp.permission === "View")
                      .map((sharedPermission) => (
                        <div
                          key={sharedPermission.entity}
                          className="k-mr-3"
                        >
                          {sharedPermission.entity}
                        </div>
                      ))
                  ) : (
                    <div>None</div>
                  )}
                  <strong>Modify Permissions: </strong>
                  {props.dataItem.sharedPermission.filter((sp) => sp.permission === "Modify")
                    .length > 0 ? (
                    props.dataItem.sharedPermission
                      .filter((sp) => sp.permission === "Modify")
                      .map((sharedPermission) => (
                        <div
                          key={sharedPermission.entity}
                          className="k-mr-3"
                        >
                          {sharedPermission.entity}
                        </div>
                      ))
                  ) : (
                    <div>None</div>
                  )}
                </div>
              )}
            </div>
            <div>
              {["List", "list"].includes(props.dataItem.datasetType) ? (
                <>
                  <div>
                    <strong>Values:</strong>
                  </div>
                  <ListView
                    data={props.dataItem.values.map(
                      (row) => row.value + (row.filterValue ? " (" + row.filterValue + ")" : "")
                    )}
                    item={ValueItemRender}
                  />
                </>
              ) : null}
            </div>
          </div>
        </Window>
      )}
    </>
  );
};

export const ViewTemplateButton = (props) => {
  const [templateVisible, setTemplateVisible] = useState(false);

  const toggleTemplateDialog = () => {
    setTemplateVisible(!templateVisible);
  };

  return (
    <>
      <ColorChangingIcon
        icon={faEye}
        initialPrimaryColor="#0078D4"
        onClick={toggleTemplateDialog}
      />

      {templateVisible && (
        <Window
          title={props.dataItem.templateName}
          onClose={toggleTemplateDialog}
          initialHeight={500}
          initialWidth={500}
        >
          <div className="rowView">
            <div>
              <strong>Owner:</strong> {props.dataItem.owner}{" "}
            </div>
            <div>
              {props.dataItem.coOwners && (
                <div>
                  <strong>Co-Owners:</strong>{" "}
                  {props.dataItem.coOwners.map((coOwner) => (
                    <div key={coOwner.user}>{coOwner.user}</div>
                  ))}
                </div>
              )}
            </div>
            <div>
              {props.dataItem.sharedPermission && (
                <div>
                  <strong>View Permissions: </strong>
                  {props.dataItem.sharedPermission.filter((sp) => sp.permission === "View").length >
                  0 ? (
                    props.dataItem.sharedPermission
                      .filter((sp) => sp.permission === "View")
                      .map((sharedPermission) => (
                        <div
                          key={sharedPermission.entity}
                          className="k-mr-3"
                        >
                          {sharedPermission.entity}
                        </div>
                      ))
                  ) : (
                    <div>None</div>
                  )}
                  <strong>Modify Permissions: </strong>
                  {props.dataItem.sharedPermission.filter((sp) => sp.permission === "Modify")
                    .length > 0 ? (
                    props.dataItem.sharedPermission
                      .filter((sp) => sp.permission === "Modify")
                      .map((sharedPermission) => (
                        <div
                          key={sharedPermission.entity}
                          className="k-mr-3"
                        >
                          {sharedPermission.entity}
                        </div>
                      ))
                  ) : (
                    <div>None</div>
                  )}
                </div>
              )}
            </div>
            <div>
              <strong>Company:</strong> {props.dataItem.company}{" "}
            </div>
            <div>
              <strong>Branch:</strong> {props.dataItem.branch}{" "}
            </div>
            <div>
              <strong>Department:</strong> {props.dataItem.department}{" "}
            </div>
            <div>
              <strong>Area:</strong> {props.dataItem.area}{" "}
            </div>
            <div>
              <strong>Sections:</strong>
            </div>
            <div
              key={`${props.dataItem.id}`}
              className="dataset-card"
            >
              <TemplateRenderer template={props.dataItem} />
            </div>
          </div>
        </Window>
      )}
    </>
  );
};
